import { Stack, Typography } from "@mui/material";
import CustomButton from "components/atoms/CustomButton";
import React, { useMemo, useState } from "react";
import VerifiedIcon from "@mui/icons-material/Verified";
import CustomTextInput from "components/atoms/CustomTextInput";
import CustomSelect from "components/atoms/Select";
import {
  GET_DESIGNATIONS,
  GET_ROLE_DESIGNATIONS,
  GET_ROLES_FOR_USERS,
  GET_TRANSACTION_TYPES,
  GET_WARDS,
} from "utils/reactQueryKeys";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { toast } from "react-toastify";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { useQueryClient } from "react-query";
const initialState = {
  specialty: "",
  amount: "",
  ward: "",
  name: "",
};

const doctorsRoleId = "662123e0ac18d4fd7678d50c";

const formatEditData = (data, type) => {
  const isAdmission = type?.toLowerCase().includes("admission");
  
  const payload = {
    amount: +data.amount,
    name: data.name,
  };

  if (type === "Consultations") {
    payload.type = "CONSULTATION";
    if (data.specialty) payload.specialty = data.specialty;
  } else if (type === "Regitration fee") {
    payload.type = "REGISTRATION FEE";
  } else if (isAdmission) {
    payload.type = "ADMISSION";
    // Only include ward if it's a valid string (not empty)
    if (data.ward && data.ward.trim() !== "") {
      payload.ward = data.ward;
    }
  }
  return payload;
};


function AddTransactionType({
  close,
  typeText,
  transactionType,

  clickedCard = {},
}) {
  const [form, setform] = useState(initialState);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const queryClient = useQueryClient();

  // Updated to handle both "Admission" and "Admissions" strings
  const isAdmissionType = transactionType?.toLowerCase().includes("admission");
  

  //get user roles
  const { data: docRoleId, isLoading: getrolesLoading } = useCustomQuery(
    [
      GET_ROLES_FOR_USERS,
      {
        limit: 1000,
      },
    ],
    {
      url: `/role?limit=100`,
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
      select: (res) => {
        return res.data.data.find(
          (role) => role.name.toLowerCase() === "doctor"
        )?.id;
      },
    }
  );

  useMemo(() => {
    if (Object.keys(clickedCard).length) {
      setform(clickedCard);
    }
  }, [clickedCard]);

  //get role designations
  const { data: designations } = useCustomQuery(
    [GET_ROLE_DESIGNATIONS, docRoleId],
    {
      url: `/designation/role/${docRoleId}`,
      method: "get",
    },
    {
      refetchOnWindowFocus: false,

      select: (res) => {
        const formartedData = res?.data?.map((role) => {
          return { name: role?.name, value: role?._id };
        });
        return formartedData;
      },
      enabled: !!docRoleId,
    }
  );

  const handleChange = (e) => {
    setform((prev) => {
      if (e.target.name === "specialty") {
        return {
          ...prev,
          [e.target.name]: e.target.value,
          name: designations.find((desig) => desig.value === e.target.value)
            .name,
        };
      } else {
        return { ...prev, [e.target.name]: e.target.value };
      }
    });
  };

  //edit transaction type
  const { mutate: editTransactionType, isLoading: editTransactionTypeLoading } =
    useCustomMutation(
      {
        url: `/transaction-type/${form?._id}`,
        method: "patch",
        data: formatEditData(form, transactionType),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(GET_TRANSACTION_TYPES);
          toast.success("Success");
          close();
        },
        onError: (error) => {
          if (typeof error?.message === "object") {
            return error?.message?.map((msg) => toast.error(msg));
          }
          return toast.error(error.message);
        },
      }
    );

  //create transaction type
  const {
    mutate: createTransactionType,
    isLoading: createTransactionTypeLoading,
  } = useCustomMutation(
    {
      url: `/transaction-type`,
      method: "post",
      data: formatEditData(form, transactionType),
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(GET_TRANSACTION_TYPES);
        toast.success("Success");
        close();
      },
      onError: (error) => {
        if (typeof error?.message === "object") {
          return error?.message?.map((msg) => toast.error(msg));
        }
        return toast.error(error.message);
      },
    }
  );

  // Delete Transaction Type Mutation
  const { mutate: deleteTransaction, isLoading: isDeleting } = useCustomMutation(
    {
      url: `/transaction-type/${form?._id}`,
      method: "delete", 
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(GET_TRANSACTION_TYPES);
        toast.success("Deleted successfully from database");
        close();
      },
      onError: (err) => toast.error(err.message),
    }
  );

  // Custom Confirmation UI
  if (isConfirmingDelete) {
    return (
      <Stack spacing={3} alignItems="center" textAlign="center" p={2}>
        <Typography variant="h6">Are you sure?</Typography>
        <Typography>
          This will permanently remove <b>{form.name || transactionType}</b> from the database.
        </Typography>
        <Stack direction="row" spacing={2}>
          <CustomButton text="Cancel" variant="light" onClick={() => setIsConfirmingDelete(false)} />
          <CustomButton 
            text={isDeleting ? "Deleting..." : "Yes, Delete"} 
            sx={{ backgroundColor: "#d32f2f", color: "white" }}
            onClick={deleteTransaction}
          />
        </Stack>
      </Stack>
    );
  }
  
  return (
    <Stack spacing={3}>
      <Stack spacing={1.5}>
        <Typography variant="displaySm">{typeText}</Typography>
      </Stack>

      <Stack>
        <CustomTextInput
          title="Transaction Type"
          value={transactionType}
          readOnly
        />

        {transactionType === "Consultations" ? (
          <CustomSelect
            options={designations || []}
            label="Specialist"
            state={form.specialty || ""}
            handleChange={handleChange}
            name="specialty"
            haveTopLabel={true}
            placeholder="Select"
          />
        ) : isAdmissionType ? (
          <CustomTextInput
            title={clickedCard?.ward ? "Ward" : "Admission Name/Title"}
            value={form.name || ""}
            name="name"
            handleChange={handleChange}
            placeholder={"e.g. General Ward Admission"}
            readOnly={typeText !== "Add New" && clickedCard?.ward}
          />
        ) : null}

        <CustomTextInput
          title="Amount"
          value={form.amount}
          name={"amount"}
          handleChange={handleChange}
          placeholder={"Select Amount"}
          sx={{ width: "50%" }}
        />
      </Stack>
      
      {/*
      <Stack>
        <CustomButton
          text={typeText === "Add New" ? "Save" : "Save Changes"}
          color="secondary"
          onClick={
            typeText === "Add New" ? createTransactionType : editTransactionType
          }
          sx={{ width: "fit-content" }}
          disabled={editTransactionTypeLoading || createTransactionTypeLoading}
        />
      </Stack>
      */}

      <Stack direction="row" spacing={2}>
        <CustomButton
          text={typeText === "Add New" ? "Save" : "Save Changes"}
          color="secondary"
          onClick={() => {
             // Use the fixed payload generator
             const payload = formatEditData(form, transactionType);
             if (typeText === "Add New") createTransactionType(payload);
             else editTransactionType(payload);
          }}
          disabled={editTransactionTypeLoading || createTransactionTypeLoading}
        />

        
        {typeText !== "Add New" && (
          <CustomButton
            text="Delete"
            variant="outlined"
            onClick={() => setIsConfirmingDelete(true)}
            sx={{ color: "#d32f2f", borderColor: "#d32f2f" }}
          />
        )}
       
      </Stack>

    </Stack>
  );
}

export default AddTransactionType;
